import { useCallback, useEffect, useState } from 'react';

/**
 * A minimal dependency-free hash router. The route is the bare path after `#/` matched
 * against the app's known routes; anything unknown falls back. Parsing is a pure function
 * so it is unit-testable without a DOM (CONVENTIONS: deterministic logic is pure).
 */
export function parseHash<T extends string>(hash: string, routes: readonly T[], fallback: T): T {
  const raw = (hash.replace(/^#\/?/, '').split('?')[0] ?? '').trim();
  return (routes as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

export function hashFor<T extends string>(route: T): string {
  return `#/${route}`;
}

/** React binding over `parseHash`; re-renders on `hashchange`. */
export function useHashRoute<T extends string>(routes: readonly T[], fallback: T): [T, (route: T) => void] {
  const [route, setRoute] = useState<T>(() => parseHash(window.location.hash, routes, fallback));

  useEffect(() => {
    const onChange = (): void => setRoute(parseHash(window.location.hash, routes, fallback));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, [routes, fallback]);

  const navigate = useCallback((next: T) => {
    window.location.hash = hashFor(next);
  }, []);

  return [route, navigate];
}
